import React from "react";
import { PaymentService } from "../../../shared/services";
import { NavigateFunction } from "react-router-dom";
import { observer } from "mobx-react";
import { debounce } from "lodash";
import {
  AuthService,
  CustomButton,
  ITableHeaders,
  ToastService,
  Loader,
  TableHeader,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  ItemsStore,
  PaymentStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { PaymentGridModel } from "../../../shared/models";
import {
  GridNames,
  ItemDropDownOptions,
  PaymentTransactionStatus,
} from "../../../shared/enums";
import {
  PaymentUtil,
  downloadBlob,
  isUsableExportFile,
  paymentExportFileName,
  showExportError,
  PaymentExportFormat,
} from "../../../shared/utils";
import {
  DashboardTopBar,
  DashboardTopRightSide,
  ExportDropDown,
  ItemContentArea,
  RenderPaymentMethod,
  TableGrid,
} from "../../../shared/components";
import { AccountSettingsStore } from "../../../shared/store";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";
import { debounceTime, Subscription } from "rxjs";

type IProps = {
  navigate: NavigateFunction;
  paymentStore: PaymentStore;
  itemStore: ItemsStore;
  merchantAccountStore: MerchantAccountStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

@observer
class Grid extends React.Component<IProps> {
  private loader: Loader = new Loader(true);
  private firstTimeLoad: boolean = true;
  private searchSubscription: Subscription;

  constructor(props: IProps) {
    super(props);
    this.searchSubscription = new Subscription();
  }

  get paymentStore() {
    return this.props.paymentStore;
  }

  get itemStore() {
    return this.props.itemStore;
  }

  componentDidMount(): void {
    this.itemStore.updateSearchValue("", ItemDropDownOptions.Payments);
    this.loadData(
      this.paymentStore.pagination.pageNumber,
      this.paymentStore.pagination.pageSize,
      this.paymentStore.searchValue,
      true
    );
    this.searchSubscription = this.paymentStore.searchSubject
      .pipe(debounceTime(500))
      .subscribe(() => {
        this.loadData(
          this.paymentStore.pagination.pageNumber,
          this.paymentStore.pagination.pageSize,
          this.paymentStore.searchValue
        );
      });
  }

  componentWillUnmount(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  loadData(
    pageNumber: number,
    pageSize: number,
    searchValue: string,
    firstTimeLoad: boolean = false
  ) {
    this.firstTimeLoad = firstTimeLoad;
    this.loader.show();
    PaymentService.getPayments(
      AuthService.authData.accountId,
      pageNumber,
      pageSize,
      searchValue
    ).subscribe({
      next: (response) => {
        this.loader.hide();
        this.paymentStore.deserializeGridData(
          response.result || [],
          response.meta
        );
      },
      error: (error) => {
        this.loader.hide();
        console.log(error);
      },
    });
  }

  debounceSearch = debounce(
    () =>
      this.loadData(
        this.paymentStore.pagination.pageNumber,
        this.paymentStore.pagination.pageSize,
        this.paymentStore.searchValue
      ),
    500
  );

  onPaginationChange = (pageNumber: number, pageSize: number) => {
    this.loadData(pageNumber, pageSize, this.paymentStore.searchValue);
  };

  goToDetailPage(data: PaymentGridModel) {
    if (data.status === PaymentTransactionStatus.Refund) return;
    this.props.navigate(`/app/payments/detail/${data.id}`);
  }

  get columns() {
    const columns: ITableHeaders<PaymentGridModel>[] = [
      {
        dataKey: "status",
        title: "Status",
        onCellRender: (data: PaymentGridModel) => {
          const colors = PaymentUtil.getColorsByStatus(data.status);

          return (
            <CustomButton bgColor={colors.bgColor} color={colors.color}>
              {PaymentTransactionStatus[data.status]}
            </CustomButton>
          );
        },
      },
      {
        dataKey: "createdDate",
        title: "Date",
        cellClassName: "table-col-20",
        className: "table-col-20",
        onCellRender: (data: PaymentGridModel) => {
          return UtilityMethods.formatDateInStringWithTime(data.createdDate);
        },
      },
      {
        dataKey: "amount",
        title: "Amount",
        cellColor: "#556bb8",
        cellClassName: "table-col-right",
        className: "table-col-right",
        onCellRender: (data: PaymentGridModel) => {
          const amount =
            "$" + AccountSettingsStore.formatAmount(data.amount, true);
          if (data.isRefund && data.success) return `(${amount})`;
          else return amount;
        },
      },
      {
        dataKey: "contact",
        title: "Contact",
        cellColor: "#556bb8",
        className: "clickable",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: PaymentGridModel) => {
          if (!data.contact) return null;
          return (
            <span
              onClick={() =>
                this.props.navigate(`/app/contacts/detail/${data.contact?.id}`)
              }
            >
              {data.contact?.displayValue}
            </span>
          );
        },
      },
      {
        dataKey: "description",
        title: "Description",
        onCellRender: (data: PaymentGridModel) => {
          return data.description || "";
        },
      },
      {
        dataKey: "paymentMethod",
        title: "Payment Method",
        onCellRender: (data: PaymentGridModel) => {
          return <RenderPaymentMethod paymentMethod={data.paymentMethod} />;
        },
      },
    ];
    return columns;
  }

  isNoData(): boolean {
    return (
      this.firstTimeLoad &&
      this.paymentStore.pagination.totalItemCount === 0 &&
      !this.loader.showLoader &&
      this.paymentStore.searchValue === ""
    );
  }

  loadPaymentData() {
    this.loadData(
      this.paymentStore.pagination.pageNumber,
      this.paymentStore.pagination.pageSize,
      this.paymentStore.searchValue,
      true
    );
  }

  private isExporting = false;

  /* Exports every payment, not just the page on screen. The API builds the file from the same
     query the grid uses, minus pagination, so the download holds the whole set. The active search
     is passed along, so a filtered grid exports the filtered set. The grid's own data and
     pagination are left untouched. */
  exportAll(format: PaymentExportFormat) {
    if (this.isExporting) return;
    this.isExporting = true;
    this.forceUpdate();
    PaymentService.exportPayments(
      AuthService.authData.accountId,
      format,
      this.paymentStore.searchValue
    ).subscribe({
      next: (blob) => {
        // Cleared before the download so a browser that refuses it cannot strand the button.
        this.isExporting = false;
        this.forceUpdate();
        if (!isUsableExportFile(blob)) {
          ToastService.showError("Error in exporting payments");
          return;
        }
        downloadBlob(blob, paymentExportFileName(format));
      },
      error: (error) => {
        this.isExporting = false;
        this.forceUpdate();
        showExportError(error, "Error in exporting payments");
      },
    });
  }

  renderDetails(node: React.ReactNode) {
    return (
      <>
        <DashboardTopBar
          itemStore={this.itemStore}
          rightContent={
            this.loader.showLoader && this.firstTimeLoad ? null : (
              <DashboardTopRightSide
                itemStore={this.itemStore}
                showSearchInput={!this.isNoData()}
                showButton={!this.isNoData()}
                onRefresh={() => this.loadPaymentData()}
                extraContent={
                  <ExportDropDown
                    onExport={(format) => this.exportAll(format)}
                    isExporting={this.isExporting}
                  />
                }
              />
            )
          }
          merchantAccountStore={this.props.merchantAccountStore}
          rolesAndPermissionStore={this.props.rolesAndPermissionStore}
        />
        <ItemContentArea
          node={node}
          itemStore={this.itemStore}
          showLoader={this.loader.showLoader}
          // className={this.isNoData() ? "content-area-only" : ""}
          showMerchantAccountNotificationBar={this.props.merchantAccountStore.hasNotificationBar()}
        />
      </>
    );
  }

  get body() {
    return this.renderDetails(
      <TableGrid
        height={
          this.paymentStore.showFilter
            ? "calc(100vh - 499px)"
            : this.props.merchantAccountStore.hasNotificationBar()
              ? "calc(100vh - 350px)"
              : ""
        }
        tableHeader={
          <TableHeader
            showFilter={this.paymentStore.showFilter}
            onToggleFilter={() => this.paymentStore.toggleFilter()}
            total={this.paymentStore.pagination.totalItemCount}
          />
        }
        totalItemCount={this.paymentStore.pagination.totalItemCount}
        onRowClick={(data: PaymentGridModel) => this.goToDetailPage(data)}
        cellSpacing={20}
        onPaginationChange={(pageNumber: number, pageSize: number) =>
          this.onPaginationChange(pageNumber, pageSize)
        }
        pagination={this.paymentStore.pagination}
        data={this.paymentStore.gridData}
        headers={this.columns}
        name={GridNames.Payment}
        hideTable={this.isNoData()}
        onRefresh={() => this.loadPaymentData()}
        rolesAndPermissionStore={this.props.rolesAndPermissionStore}
        countriesWithState={this.itemStore.countryStore.countriesWithState}
      />
    );
  }
  render(): React.ReactNode {
    return <>{this.body}</>;
  }
}
export default withRouter(Grid);
